import { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import conferenceBackground from "../../../../../../../../../assets/conference-pattern.png";
import { Home } from "lucide-react";

interface CountdownTime {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
}

interface HeaderSectionProps {
  eventTitle: string;
  eventDate: string;
  eventVenue: string;
  eventCity: string;
  targetDate?: string;
  backgroundImage?: string;
}

export default function HeaderSection({
  eventTitle,
  eventDate,
  eventVenue,
  eventCity,
  targetDate,
  backgroundImage,
}: HeaderSectionProps) {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));

  const [timeLeft, setTimeLeft] = useState<CountdownTime>({
    days: "000",
    hours: "00",
    minutes: "00",
    seconds: "00",
  });

  useEffect(() => {
    if (!targetDate) return;

    const eventDate = new Date(targetDate).getTime();

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const difference = eventDate - now;

      if (difference <= 0) {
        clearInterval(timer);
        setTimeLeft({
          days: "000",
          hours: "00",
          minutes: "00",
          seconds: "00",
        });
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeLeft({
        days: String(days).padStart(3, "0"),
        hours: String(hours).padStart(2, "0"),
        minutes: String(minutes).padStart(2, "0"),
        seconds: String(seconds).padStart(2, "0"),
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  const location = [eventVenue, eventCity].filter(Boolean).join(", ");

  return (
    <Box
      sx={{
        width: "100%",
        height: { xs: "auto", md: 240 },
        minHeight: 240,
        backgroundImage: `url(${backgroundImage || conferenceBackground})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        textAlign: "left",
        px: 0,
        mb: { xs: 3, md: 4 },
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          py: { xs: 3, md: 0 },
          px: { xs: 2, md: 6 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            width: "100%",
            mt: { xs: 1, md: 0 },
            mb: { xs: 1, md: 0 },
            gap: 3,
          }}
        >
          {/* LEFT SIDE */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              variant={isMobile ? "h4" : "h1"}
              sx={{
                fontWeight: isMobile ? 700 : 800,
                letterSpacing: 0.5,
              }}
            >
              {eventTitle || "Ulendo Leadership Breakfast"}
            </Typography>

            <Typography sx={{ fontSize: { xs: "1rem", md: "1.2rem" } }}>
              {eventDate}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                px: 2,
                py: 0.5,
                borderRadius: "20px",
                border: "1px solid rgba(255,255,255,0.4)",
                fontSize: "0.95rem",
                gap: 0.5,
                opacity: 0.9,
                width: "fit-content",
              }}
            >
              <Home style={{ width: 16, height: 16 }} />
              <Typography>
                {location || "Holiday Inn, Harare, Zimbabwe"}
              </Typography>
            </Box>
          </Box>

          {/* RIGHT SIDE - COUNTDOWN */}
          {targetDate && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 1.5, md: 2 },
                mt: { xs: 2, md: 0 },
                fontWeight: 600,
                fontSize: "1rem",
                textAlign: "center",
              }}
            >
              <Box>
                <Typography variant={isMobile ? "h5" : "h3"}>
                  {timeLeft.days}
                </Typography>
                <Typography variant="caption">Days</Typography>
              </Box>
              <Box>
                <Typography variant={isMobile ? "h5" : "h3"}>
                  {timeLeft.hours}
                </Typography>
                <Typography variant="caption">Hrs</Typography>
              </Box>
              <Box>
                <Typography variant={isMobile ? "h5" : "h3"}>
                  {timeLeft.minutes}
                </Typography>
                <Typography variant="caption">Min</Typography>
              </Box>
              <Box>
                <Typography variant={isMobile ? "h5" : "h3"}>
                  {timeLeft.seconds}
                </Typography>
                <Typography variant="caption">Sec</Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}
